import React, { useRef, useEffect, useState } from 'react';
import { LunarLandmark } from '../../types';
import './MoonImage3D.css';

interface MoonImage3DProps {
  landmark: LunarLandmark;
  size?: 'small' | 'large';
  onClick?: () => void;
}

interface Feature3D {
  type: 'mare' | 'crater' | 'highland';
  latitude: number;   // -90 to 90
  longitude: number;  // -180 to 180
  size: number;       // radius as fraction of moon radius
  depth: number;      // for craters (0-1)
  opacity: number;    // base opacity
  ellipseRx?: number; // for elliptical features
  ellipseRy?: number;
}

interface Point3D {
  x: number;
  y: number;
  z: number;
}

export const MoonImage3D: React.FC<MoonImage3DProps> = ({ 
  landmark, 
  size = 'small',
  onClick 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const lastPos = useRef({ x: 0, y: 0 });
  const animationFrame = useRef<number>();

  const moonSize = size === 'small' ? 80 : 500;

  // Generate lunar features distributed around sphere
  const features: Feature3D[] = [
    // Large Maria (Seas) - dark basalt plains
    { type: 'mare', latitude: 8.5, longitude: 31.4, size: 0.15, depth: 0, opacity: 0.8, ellipseRx: 1.0, ellipseRy: 1.2 },
    { type: 'mare', latitude: 28.0, longitude: 17.5, size: 0.12, depth: 0, opacity: 0.75, ellipseRx: 1.1, ellipseRy: 0.9 },
    { type: 'mare', latitude: 32.8, longitude: -15.6, size: 0.18, depth: 0, opacity: 0.78, ellipseRx: 1.3, ellipseRy: 1.1 },
    { type: 'mare', latitude: 19.0, longitude: -57.0, size: 0.14, depth: 0, opacity: 0.76, ellipseRx: 1.0, ellipseRy: 1.15 },
    { type: 'mare', latitude: 17.0, longitude: 61.7, size: 0.11, depth: 0, opacity: 0.74, ellipseRx: 0.95, ellipseRy: 1.0 },
    { type: 'mare', latitude: -15.0, longitude: 35.0, size: 0.10, depth: 0, opacity: 0.72, ellipseRx: 1.0, ellipseRy: 1.05 },
    { type: 'mare', latitude: 5.0, longitude: -150.0, size: 0.13, depth: 0, opacity: 0.75, ellipseRx: 1.1, ellipseRy: 1.0 },
    
    // Large Impact Craters
    { type: 'crater', latitude: -43.3, longitude: -11.2, size: 0.09, depth: 0.85, opacity: 0.9, ellipseRx: 1.0, ellipseRy: 1.1 },
    { type: 'crater', latitude: 51.6, longitude: -9.3, size: 0.07, depth: 0.8, opacity: 0.85, ellipseRx: 1.05, ellipseRy: 1.0 },
    { type: 'crater', latitude: 9.6, longitude: -20.1, size: 0.08, depth: 0.82, opacity: 0.87, ellipseRx: 0.95, ellipseRy: 1.08 },
    { type: 'crater', latitude: -58.4, longitude: -14.7, size: 0.085, depth: 0.88, opacity: 0.91, ellipseRx: 1.0, ellipseRy: 1.0 },
    { type: 'crater', latitude: 23.7, longitude: -47.4, size: 0.075, depth: 0.84, opacity: 0.86, ellipseRx: 1.02, ellipseRy: 1.05 },
    { type: 'crater', latitude: -89.9, longitude: 0.0, size: 0.06, depth: 0.9, opacity: 0.92, ellipseRx: 1.0, ellipseRy: 1.0 },
    { type: 'crater', latitude: 88.6, longitude: 30.0, size: 0.055, depth: 0.87, opacity: 0.89, ellipseRx: 1.0, ellipseRy: 1.0 },
    
    // Medium Craters
    { type: 'crater', latitude: 35.0, longitude: 75.0, size: 0.045, depth: 0.75, opacity: 0.80, ellipseRx: 1.05, ellipseRy: 1.1 },
    { type: 'crater', latitude: -25.0, longitude: 105.0, size: 0.048, depth: 0.78, opacity: 0.82, ellipseRx: 0.98, ellipseRy: 1.05 },
    { type: 'crater', latitude: 15.0, longitude: -120.0, size: 0.042, depth: 0.76, opacity: 0.81, ellipseRx: 1.0, ellipseRy: 1.08 },
    { type: 'crater', latitude: -50.0, longitude: 90.0, size: 0.046, depth: 0.79, opacity: 0.83, ellipseRx: 1.03, ellipseRy: 1.0 },
    { type: 'crater', latitude: 60.0, longitude: -85.0, size: 0.044, depth: 0.77, opacity: 0.80, ellipseRx: 1.0, ellipseRy: 1.06 },
    
    // Small Craters
    { type: 'crater', latitude: 45.0, longitude: 25.0, size: 0.028, depth: 0.70, opacity: 0.75, ellipseRx: 1.0, ellipseRy: 1.05 },
    { type: 'crater', latitude: -35.0, longitude: -65.0, size: 0.032, depth: 0.72, opacity: 0.76, ellipseRx: 1.02, ellipseRy: 1.0 },
    { type: 'crater', latitude: 20.0, longitude: 140.0, size: 0.025, depth: 0.68, opacity: 0.73, ellipseRx: 1.0, ellipseRy: 1.04 },
    { type: 'crater', latitude: -60.0, longitude: -170.0, size: 0.030, depth: 0.71, opacity: 0.75, ellipseRx: 1.0, ellipseRy: 1.0 },
    { type: 'crater', latitude: 75.0, longitude: 160.0, size: 0.027, depth: 0.69, opacity: 0.74, ellipseRx: 1.0, ellipseRy: 1.03 },
    { type: 'crater', latitude: -15.0, longitude: -25.0, size: 0.022, depth: 0.66, opacity: 0.72, ellipseRx: 1.0, ellipseRy: 1.02 },
    { type: 'crater', latitude: 55.0, longitude: 110.0, size: 0.024, depth: 0.67, opacity: 0.73, ellipseRx: 1.0, ellipseRy: 1.0 },
    
    // Highlands (lighter regions)
    { type: 'highland', latitude: 50.0, longitude: 20.0, size: 0.08, depth: 0, opacity: 0.30, ellipseRx: 1.0, ellipseRy: 1.15 },
    { type: 'highland', latitude: -45.0, longitude: 45.0, size: 0.09, depth: 0, opacity: 0.28, ellipseRx: 1.1, ellipseRy: 1.0 },
    { type: 'highland', latitude: 65.0, longitude: -60.0, size: 0.07, depth: 0, opacity: 0.32, ellipseRx: 1.05, ellipseRy: 1.08 },
    { type: 'highland', latitude: -30.0, longitude: 120.0, size: 0.075, depth: 0, opacity: 0.29, ellipseRx: 1.0, ellipseRy: 1.12 },
    { type: 'highland', latitude: 38.4, longitude: 10.0, size: 0.06, depth: 0, opacity: 0.27, ellipseRx: 1.08, ellipseRy: 1.0 },
    { type: 'highland', latitude: -70.0, longitude: -140.0, size: 0.08, depth: 0, opacity: 0.31, ellipseRx: 1.0, ellipseRy: 1.1 },
  ];

  // Convert lat/lon to 3D Cartesian coordinates
  const latLonTo3D = (lat: number, lon: number, radius: number): Point3D => {
    const phi = (90 - lat) * Math.PI / 180;
    const theta = (lon + 180) * Math.PI / 180;
    
    return {
      x: radius * Math.sin(phi) * Math.cos(theta),
      y: radius * Math.cos(phi),
      z: radius * Math.sin(phi) * Math.sin(theta)
    };
  };

  // Rotate point around Y-axis (horizontal rotation)
  const rotateY = (point: Point3D, angle: number): Point3D => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      x: point.x * cos - point.z * sin,
      y: point.y,
      z: point.x * sin + point.z * cos
    };
  };

  // Rotate point around X-axis (vertical rotation)
  const rotateX = (point: Point3D, angle: number): Point3D => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      x: point.x,
      y: point.y * cos - point.z * sin,
      z: point.y * sin + point.z * cos
    };
  };

  // Project 3D point to 2D with perspective
  const project = (point: Point3D, canvasSize: number) => {
    const radius = canvasSize / 2;
    const fov = 800; // Field of view
    const scale = fov / (fov + point.z);
    
    return {
      x: canvasSize / 2 + point.x * scale,
      y: canvasSize / 2 + point.y * scale,
      scale: scale,
      visible: point.z > -radius * 0.95 // On front hemisphere
    };
  };

  // Render the moon
  const render = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const displaySize = moonSize;
    canvas.width = displaySize * dpr;
    canvas.height = displaySize * dpr;
    canvas.style.width = `${displaySize}px`;
    canvas.style.height = `${displaySize}px`;
    ctx.scale(dpr, dpr);

    const radius = displaySize / 2;

    // Clear canvas
    ctx.clearRect(0, 0, displaySize, displaySize);

    // Draw base moon sphere with gradient
    const gradient = ctx.createRadialGradient(
      displaySize / 2, displaySize / 2, 0,
      displaySize / 2, displaySize / 2, radius
    );
    gradient.addColorStop(0, '#3a4556');
    gradient.addColorStop(0.7, '#2a3340');
    gradient.addColorStop(1, '#1a1f2e');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(displaySize / 2, displaySize / 2, radius, 0, Math.PI * 2);
    ctx.fill();

    // Transform and sort features by depth
    const transformedFeatures = features.map(feature => {
      let point = latLonTo3D(feature.latitude, feature.longitude, radius);
      point = rotateY(point, rotation.y);
      point = rotateX(point, rotation.x);
      const projected = project(point, displaySize);
      
      return {
        feature,
        projected,
        depth: point.z
      };
    });

    // Sort by depth (back to front)
    transformedFeatures.sort((a, b) => a.depth - b.depth);

    // Draw features
    transformedFeatures.forEach(({ feature, projected }) => {
      if (!projected.visible) return;

      const featureRadius = feature.size * radius * projected.scale;
      const opacity = feature.opacity * Math.max(0.3, projected.scale);

      if (feature.type === 'mare') {
        // Dark basalt plain
        const mareGradient = ctx.createRadialGradient(
          projected.x, projected.y, 0,
          projected.x, projected.y, featureRadius
        );
        mareGradient.addColorStop(0, `rgba(37, 42, 54, ${opacity})`);
        mareGradient.addColorStop(1, `rgba(21, 26, 38, ${opacity})`);
        
        ctx.fillStyle = mareGradient;
        ctx.beginPath();
        ctx.ellipse(
          projected.x, projected.y,
          featureRadius * (feature.ellipseRx || 1),
          featureRadius * (feature.ellipseRy || 1),
          0, 0, Math.PI * 2
        );
        ctx.fill();
      } else if (feature.type === 'crater') {
        // Impact crater with rim
        const craterOpacity = opacity * feature.depth;
        
        // Crater floor (dark)
        ctx.fillStyle = `rgba(15, 20, 25, ${craterOpacity})`;
        ctx.beginPath();
        ctx.ellipse(
          projected.x, projected.y,
          featureRadius * (feature.ellipseRx || 1) * 0.85,
          featureRadius * (feature.ellipseRy || 1) * 0.85,
          0, 0, Math.PI * 2
        );
        ctx.fill();
        
        // Crater rim (lighter edge)
        const rimGradient = ctx.createRadialGradient(
          projected.x, projected.y, featureRadius * 0.7,
          projected.x, projected.y, featureRadius
        );
        rimGradient.addColorStop(0, 'rgba(74, 85, 102, 0)');
        rimGradient.addColorStop(0.6, `rgba(74, 85, 102, ${opacity * 0.4})`);
        rimGradient.addColorStop(1, 'rgba(58, 69, 86, 0)');
        
        ctx.fillStyle = rimGradient;
        ctx.beginPath();
        ctx.ellipse(
          projected.x, projected.y,
          featureRadius * (feature.ellipseRx || 1),
          featureRadius * (feature.ellipseRy || 1),
          0, 0, Math.PI * 2
        );
        ctx.fill();
      } else if (feature.type === 'highland') {
        // Lighter highland region
        ctx.fillStyle = `rgba(66, 73, 90, ${opacity})`;
        ctx.beginPath();
        ctx.ellipse(
          projected.x, projected.y,
          featureRadius * (feature.ellipseRx || 1),
          featureRadius * (feature.ellipseRy || 1),
          0, 0, Math.PI * 2
        );
        ctx.fill();
      }
    });

    // Draw landmark highlight
    const landmarkPoint = latLonTo3D(landmark.latitude, landmark.longitude, radius);
    const rotatedLandmark = rotateX(rotateY(landmarkPoint, rotation.y), rotation.x);
    const projectedLandmark = project(rotatedLandmark, displaySize);

    if (projectedLandmark.visible) {
      const highlightRadius = landmark.imageHighlight.radius * radius * projectedLandmark.scale;
      const highlightOpacity = Math.max(0.3, projectedLandmark.scale);
      
      // Pulsing effect
      const pulse = 0.85 + Math.sin(Date.now() / 500) * 0.15;
      
      ctx.strokeStyle = `rgba(0, 255, 255, ${highlightOpacity * 0.6 * pulse})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(projectedLandmark.x, projectedLandmark.y, highlightRadius * pulse, 0, Math.PI * 2);
      ctx.stroke();
      
      ctx.fillStyle = `rgba(0, 255, 255, ${highlightOpacity * 0.2 * pulse})`;
      ctx.fill();
      
      // Center dot
      ctx.fillStyle = `rgba(0, 255, 255, ${highlightOpacity})`;
      ctx.beginPath();
      ctx.arc(projectedLandmark.x, projectedLandmark.y, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Border glow
    ctx.strokeStyle = 'rgba(0, 255, 255, 0.5)';
    ctx.lineWidth = 2;
    ctx.shadowBlur = 20;
    ctx.shadowColor = 'rgba(0, 255, 255, 0.5)';
    ctx.beginPath();
    ctx.arc(displaySize / 2, displaySize / 2, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;
  };

  // Animation loop
  useEffect(() => {
    const animate = () => {
      // Apply velocity (momentum)
      if (Math.abs(velocity.x) > 0.001 || Math.abs(velocity.y) > 0.001) {
        setRotation(prev => ({
          x: prev.x + velocity.x,
          y: prev.y + velocity.y
        }));
        
        // Damping
        setVelocity(prev => ({
          x: prev.x * 0.95,
          y: prev.y * 0.95
        }));
      }
      
      render();
      animationFrame.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [rotation, velocity]);

  // Mouse handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setVelocity({ x: 0, y: 0 }); // Stop momentum
    dragStart.current = { x: e.clientX, y: e.clientY };
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - lastPos.current.x;
    const deltaY = e.clientY - lastPos.current.y;

    setRotation(prev => ({
      x: prev.x - deltaY * 0.01,
      y: prev.y + deltaX * 0.01
    }));

    // Track velocity for momentum
    setVelocity({
      x: -deltaY * 0.001,
      y: deltaX * 0.001
    });

    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    // Only trigger onClick if not dragged
    const dragDistance = Math.sqrt(
      Math.pow(e.clientX - dragStart.current.x, 2) +
      Math.pow(e.clientY - dragStart.current.y, 2)
    );
    
    if (dragDistance < 5 && onClick) {
      onClick();
    }
  };

  return (
    <canvas
      ref={canvasRef}
      className={`moon-image-3d moon-image-${size} ${isDragging ? 'dragging' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={`3D Moon showing ${landmark.displayName}`}
      title={onClick ? `Click to view ${landmark.displayName} - Drag to rotate` : 'Drag to rotate'}
    />
  );
};

